import { useEffect } from "react";
import "./modalstorecart.css";
import { formatMoney } from '@/lib';
import {
  resolverPrecioProducto,
  resolverBadgesDescuento,
  resolverSubtotalProducto,
} from '@/lib/precioProducto';
import { BlueTag } from "./BlueTag";
import { RedTag } from "./RedTag";
import { OfferTag } from "./OfferTag";
import { useProductStore } from "@/_zustand/store";
import { useMachine } from "@/_zustand/machine";
import { useNavigate } from "react-router-dom";
import { useCompanyFlow } from "@/hooks/useCompanyFlow";
import toast from "react-hot-toast";


export const ModalStoreCart = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { getCompanyRoute } = useCompanyFlow();
const { products, removeFromCart, addToCart, calculateTotals, setDiscount } = useProductStore();
  const { machineSelected, logoMachine, machine, nameEmpresa, addressMachine} = useMachine();
  const productsByMachine= (products.filter(product => product.maquina_id === machineSelected && Number(product.producto_cant) > 0));

  // Si se vacía el carrito de esta máquina (ej. al eliminar el último
  // producto con el basurero) el modal quedaría con el área de contenido
  // en blanco sin este efecto, en vez de cerrarse como ya hace StorePage.jsx.
  useEffect(() => {
    if (isOpen && productsByMachine.length === 0) {
      onClose();
    }
  }, [isOpen, productsByMachine.length, onClose]);

  const handleRemoveProduct = (id) => {
    removeFromCart(id, machineSelected);
    calculateTotals(machineSelected);
    setDiscount(0, 0, null);
  }

  const handleQuantityChange = (actionName, productToUpdate) => {
  if (actionName === "minus" && productToUpdate.producto_cant <= 1) {
    handleRemoveProduct(productToUpdate.producto_id);
    return;
  }

  let newAmount = productToUpdate.producto_cant;

  if (actionName === "plus") {
    const limiteStock = Math.min(
      productToUpdate.stock ?? Infinity,
      productToUpdate.stock_maximo ?? Infinity,
    );
    if (newAmount + 1 > limiteStock) {
      toast.error(
        `No puedes agregar más de ${limiteStock} unidades de este producto.`,
      );
      return;
    }
    newAmount = newAmount + 1;
  } else if (actionName === "minus" && newAmount > 1) {
    newAmount = newAmount - 1;
  }

  let updatedProduct = {
    ...productToUpdate,
    producto_cant: newAmount,
    maquina_id: machineSelected,
    logo_machine: logoMachine,
    name_empresa: nameEmpresa,
    address_machine: addressMachine
  };

  addToCart(updatedProduct, machineSelected);
  calculateTotals(machineSelected);

  };

  const handleClickCarrito = () => {
    navigate(getCompanyRoute('cart'), { replace: true });
  }

  const ModalHeader = () => {
    return (
      <div className="modal-header-store">
        <div className="store-logo-wrapper">
          <img
            src={logoMachine}
            alt={machine.empresa_nombre}
            className="modal-store-logo"
          />
        </div>
        <div className="store-info">
          <h2 className="store-name">{machine.empresa_nombre}</h2>
          <p className="store-description">{machine.comuna_nombre}</p>
          <p className="store-address">{machine.maquina_direccion}</p>
        </div>
      </div>
    );
  };

  const ProductCard = ({ product }) => {
    const { precioFinal, precioOriginal, tieneDescuento } =
      resolverPrecioProducto(product);
    const {
      subtotal,
      subtotalOriginal,
      tieneDescuento: subtotalTieneDescuento,
    } = resolverSubtotalProducto(product, product.producto_cant);
    const badges = resolverBadgesDescuento(product);
    return (
      <div className="modal-product-card">
        <div className="modal-product-image-wrapper">
          <img
            src={product.producto_foto}
            alt={product.producto_nombre}
            className="modal-product-image"
          />
        </div>
        <div className="modal-product-details">
          <h3 className="modal-product-name">{product.producto_nombre}</h3>

          <div className="modal-price-info">
            <span className="modal-current-price">
              {formatMoney(precioFinal)} CLP
            </span>
            {tieneDescuento && (
              <span className="modal-old-price">
                {formatMoney(precioOriginal)} CLP
              </span>
            )}

            {/* Subtotal de la línea SOLO cuando el precio unitario no alcanza
                a explicar el descuento — el caso real es el 2x1, que no baja
                el precio por unidad (arriba tieneDescuento es false) pero sí
                cobra menos unidades, y sin esto quedaba invisible en este
                modal. Con un descuento de precio unitario el tachado de
                arriba ya lo comunica, así que repetirlo acá sería ruido. */}
            {!tieneDescuento && subtotalTieneDescuento && (
              <span className="modal-current-price">
                Subtotal {formatMoney(subtotal)} CLP{" "}
                <span className="modal-old-price">
                  {formatMoney(subtotalOriginal)} CLP
                </span>
              </span>
            )}

            {badges.length > 0 && (
              <div className="cart-tags">
                {badges.map((badge, i) =>
                  badge.tipo === "porcentaje" ||
                  badge.tipo === "porcentaje-oferta" ? (
                    <RedTag key={i} percentage={badge.percentage} />
                  ) : badge.tipo === "2x1" ? (
                    <BlueTag key={i} />
                  ) : (
                    <OfferTag key={i} />
                  ),
                )}
              </div>
            )}
          </div>
        </div>
        <div className="cart-product-actions">
          
          <div className="cart-quantity">
          {product.producto_cant == 1 && (
            <button
              className="cart-remove-btn"
              onClick={() => handleRemoveProduct(product.producto_id)}
            >
              <i className="fas fa-trash"></i>
            </button>
          )}
            {product.producto_cant > 1 && (
              <button onClick={() => handleQuantityChange("minus", product)}>–</button>
            )}
            <span>{product.producto_cant}</span>
            <button onClick={() => handleQuantityChange("plus", product)}>+</button>
          </div>
        </div>
      </div>
    );
  };

  const ProductList = ({ products }) => {
    return (
      <div className="modal-product-list">
        {products && products.map((product) => (
          <ProductCard key={product.producto_id} product={product} />
        ))}
      </div>
    );
  };

  const ModalFooter = ({ onContinue, handleClickCarrito }) => {
    return (
      <div className="modal-footer-store footer-buttons-row">
      <button
        type="button"
        className="go-to-cart-modal"
        onClick={ handleClickCarrito }
      >
        Ir al carrito
        <span className="cart-icon-modal" />
      </button>

      <button
        type="button"
        className="continue-button"
        onClick={onContinue}
      >
        Continuar
      </button>
    </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay-store">
      <div className="modal-content-store">
        <button className="modal-close-button" onClick={onClose}>
          &times;
        </button>
        <ModalHeader/>
        <ProductList products={productsByMachine} />
        <ModalFooter onContinue={onClose}  handleClickCarrito={handleClickCarrito}/>
      </div>
    </div>
  );
};
